// Die INHALTS-Stylesheets des Drawers laden bewusst asynchron (media="print"-Swap
// in layout/theme.liquid, Begruendung steht dort). Das traegt, solange der Drawer zu
// ist — im Moment des Oeffnens muessen sie aber angewandt sein, sonst erscheint der
// Inhalt ungestylt und springt beim Nachladen um. Hier schalten wir sie hart auf
// media="all" und warten notfalls kurz auf den Ladeabschluss.
const CART_CSS_SELECTOR = 'link[data-ap-cart-css]';
const CART_CSS_MAX_WAIT = 400;

function whenCartStylesReady(callback) {
  const links = Array.from(document.querySelectorAll(CART_CSS_SELECTOR));
  links.forEach((link) => {
    if (link.media !== 'all') link.media = 'all';
  });

  // link.sheet ist erst gesetzt, wenn die Datei da ist — unabhaengig von media.
  const pending = links.filter((link) => !link.sheet);
  if (pending.length === 0) {
    callback();
    return;
  }

  let settled = false;
  const finish = () => {
    if (settled) return;
    settled = true;
    callback();
  };

  // Ein totes Netz darf den Drawer nie blockieren: die Wartezeit ist gedeckelt.
  const timer = setTimeout(finish, CART_CSS_MAX_WAIT);
  let remaining = pending.length;
  const onSettled = () => {
    remaining -= 1;
    if (remaining > 0) return;
    clearTimeout(timer);
    finish();
  };
  pending.forEach((link) => {
    link.addEventListener('load', onSettled, { once: true });
    link.addEventListener('error', onSettled, { once: true });
  });
}

// Elemente, die beim Oeffnen NICHT inert werden duerfen, obwohl sie Geschwister
// der Schublade sind: <cart-disclosure-modal> haengt sich selbst an <body>
// (assets/cart-disclosure-modal.js) und wird AUS der Schublade heraus geoeffnet —
// inert waere der Dialog sichtbar, aber nicht bedienbar. Skripte, Stile und
// Vorlagen haben ohnehin keinen Fokus; sie bleiben unberuehrt, damit der
// Zustand der Seite so klein wie moeglich veraendert wird.
const INERT_SKIP_TAGS = ['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE', 'NOSCRIPT', 'CART-DISCLOSURE-MODAL'];

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Escape schliesst die Schublade — ausser, er schliesst gerade die Details der
    // Vertrauenszeile (Popover im Fuss, R2 28.09.2026): der Browser schliesst das
    // Popover schon beim keydown, das keyup kaeme sonst hier an und schloesse die
    // Schublade gleich mit.
    this.addEventListener('keydown', (evt) => {
      if (evt.code !== 'Escape') return;
      let offen = false;
      try {
        offen = !!this.querySelector(':popover-open');
      } catch (e) {
        offen = false;
      }
      this.escSchliesstPopover = offen;
    });
    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;
      if (this.escSchliesstPopover) {
        this.escSchliesstPopover = false;
        return;
      }
      this.close();
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    // "Weiter shoppen" in der leeren Schublade (und jedes kuenftige Element mit
    // data-ap-drawer-close) schliesst die Schublade. Delegiert am HOST: der
    // Inhalt wird bei jedem Warenkorb-Update per innerHTML ersetzt, ein am
    // Knopf gebundener Zuhoerer waere danach weg.
    this.addEventListener('click', (evt) => {
      const target = evt.target;
      if (target && typeof target.closest === 'function' && target.closest('[data-ap-drawer-close]')) {
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();
  }

  /*
   * FOKUSFALLE UEBER `inert` (W14). Dawns trapFocus lenkt Tab nur am ersten und
   * LETZTEN fokussierbaren Element um. Unter 1.100 px ist das letzte Element der
   * Liste der "+"-Knopf im Desktop-Begleitpanel — unsichtbar, also nie
   * fokussiert; die Umlenkung griff nie, und nach den Express-Knoepfen (die in
   * iframes/Shadow-DOM liegen und deren keydown das Dokument gar nicht
   * erreicht) lief der Fokus in die verdeckte Seite: Ankuendigung, Menue,
   * Brotkrumen. Gemessen auf 390 und 1024 px.
   *
   * `inert` auf allen Geschwistern der Schublade (und der ihrer Vorfahren bis
   * <body>) macht die Seite fuer Tab, Klick und Vorleseprogramme unerreichbar —
   * unabhaengig davon, was in der Schublade das letzte Element ist. Nur Elemente,
   * die WIR inert gesetzt haben, werden beim Schliessen zurueckgenommen; was
   * vorher schon inert war, bleibt es. Dawns globales trapFocus bleibt unberuehrt
   * (Menue- und Suchschubladen nutzen es weiter).
   */
  setPageInert(on) {
    if (!on) {
      (this.inertNodes || []).forEach((node) => {
        node.inert = false;
      });
      this.inertNodes = null;
      return;
    }
    if (this.inertNodes || !('inert' in HTMLElement.prototype)) return;
    const nodes = [];
    let node = this;
    while (node && node.parentElement && node !== document.body) {
      const parent = node.parentElement;
      Array.from(parent.children).forEach((sibling) => {
        if (sibling === node || sibling.inert || INERT_SKIP_TAGS.includes(sibling.tagName)) return;
        sibling.inert = true;
        nodes.push(sibling);
      });
      node = parent;
    }
    this.inertNodes = nodes;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    // Jeder Oeffnungswunsch bekommt eine Nummer: schliesst der Kunde waehrend der
    // (gedeckelten) CSS-Wartezeit wieder, darf der spaete Rueckruf nicht doch noch
    // aufziehen.
    this.openRequest = (this.openRequest || 0) + 1;
    const openRequest = this.openRequest;
    setTimeout(() => {
      whenCartStylesReady(() => {
        if (this.openRequest !== openRequest) return;

        // Alles, was den SICHTBAREN Zustand betrifft, haengt bewusst hier drin und
        // nicht davor: sonst laege die Scroll-Sperre schon auf der Seite, waehrend
        // noch nichts zu sehen ist, der {once:true}-Horcher koennte ein fremdes
        // transitionend aus dem Drawer-Inneren abfangen und dabei verpuffen, und
        // das Sicht-Ereignis meldete eine Ansicht, die noch gar nicht stattfand.
        // Liegt das CSS schon vor — der Normalfall — ruft whenCartStylesReady
        // synchron zurueck, der Ablauf ist dann exakt der alte.
        this.addEventListener(
          'transitionend',
          () => {
            const containerToTrapFocusOn = this.classList.contains('is-empty')
              ? this.querySelector('.drawer__inner-empty')
              : document.getElementById('CartDrawer');
            const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
            trapFocus(containerToTrapFocusOn, focusElement);
          },
          { once: true },
        );

        document.body.classList.add('overflow-hidden');
        this.classList.add('animate', 'active');
        this.sichtfeldBeobachten(true);
        // Im selben Moment, in dem die Schublade sichtbar wird — nicht erst im
        // transitionend: dazwischen laege ein Fenster, in dem Tab noch in die
        // Seite liefe, und ohne Uebergang (reduzierte Bewegung) kaeme das
        // Ereignis womoeglich nie.
        this.setPageInert(true);

        // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
        // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
        // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
        this.querySelector('cart-drawer-items')?.dispatchViewEvent();
      });
    });
  }

  close() {
    // Entwertet einen noch laufenden Oeffnungswunsch (siehe open()).
    this.openRequest = (this.openRequest || 0) + 1;
    this.classList.remove('active');
    // Der Hinweis gehoert zu genau EINEM Hinzufuegen — beim naechsten Oeffnen
    // (Warenkorb-Symbol) stuende er sonst veraltet ueber dem Korb.
    this.zeigeHinweis('');
    // ZUERST die Seite wieder freigeben, DANN den Fokus zurueckgeben: der
    // Ausloeser (Warenkorb-Symbol im Kopf) ist bis hierher inert und nimmt
    // keinen Fokus an — removeTrapFocus liefe sonst ins Leere.
    this.setPageInert(false);
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.sichtfeldBeobachten(false);
  }

  // Bildschirmtastatur (Mengen-, Notiz- oder Rabattfeld) verkleinert das
  // Sichtfeld. Die Schublade folgt dem visualViewport (--ap-vv-h auf der
  // Schublade, component-cart-drawer.css), damit der feste Fuss mit „Sicher zur
  // Kasse" ueber der Tastatur bleibt (Betreiber-Pflichtbefund H3, 27.09.2026).
  // Ohne visualViewport bleibt die Hoehe 100 % — wie bisher.
  sichtfeldBeobachten(an) {
    const vv = window.visualViewport;
    if (!vv) return;
    if (!this.onSichtfeld) {
      this.onSichtfeld = () => {
        this.style.setProperty('--ap-vv-h', `${Math.round(vv.height)}px`);
      };
    }
    vv.removeEventListener('resize', this.onSichtfeld);
    if (an) {
      this.onSichtfeld();
      vv.addEventListener('resize', this.onSichtfeld);
    } else {
      this.style.removeProperty('--ap-vv-h');
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // "is-empty" sitzt am HOST-Element <cart-drawer> (snippets/cart-drawer.liquid),
    // nicht auf .drawer__inner. Dawn raeumte die Klasse an der falschen Stelle weg,
    // also blieb sie nach dem Befuellen stehen und drei Regeln griffen weiter:
    // .is-empty .cart__contents blendete das ganze Positionsformular aus
    // (component-cart.css), cart-drawer.is-empty .drawer__header den Kopf, und
    // .drawer__inner wurde zum einzeiligen, randlosen Grid — der Drawer ging
    // sichtbar kaputt auf und heilte erst beim naechsten Seitenaufbau.
    // Betroffen war jeder Weg, der HIER hereinkommt statt ueber product-form.js:
    // Sticky-Kaufleiste und Satz-Preis (automotive.js), "Wird oft benoetigt"
    // (needed-with.js) — und nur dann, wenn der Warenkorb beim Seitenaufbau leer war.
    //
    // ABGESICHERT (26.09.2026): Dawn griff ohne jede Pruefung auf
    // parsedState.sections[id] und .querySelector(selector).innerHTML zu. Fehlte
    // eine Section in der Antwort (null bei einem Renderfehler, gar keine
    // sections bei einem Fehlerkoerper), warf das VOR open() — der Artikel lag
    // im Korb, die Schublade blieb zu, sichtbar war nichts. Jetzt wird nur
    // gezeichnet, was wirklich da ist, und geoeffnet wird immer.
    const sections = (parsedState && parsedState.sections) || {};
    if (sections['cart-drawer']) {
      this.classList.remove('is-empty');
      this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    }
    this.productId = parsedState && parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !sections[section.id]) return;
      const innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
      if (innerHTML !== null) sectionElement.innerHTML = innerHTML;
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html || '', 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  }

  /*
   * HINWEIS NACH DEM HINZUFUEGEN (window.ApCartAdd.kauf, assets/automotive.js):
   * "Nur noch 3 Stueck verfuegbar – 3 liegen jetzt im Warenkorb" bzw. "Sie
   * haben bereits alle verfuegbaren 3 Stueck im Warenkorb". Er steht direkt
   * unter dem Kopf der Schublade (snippets/cart-drawer.liquid,
   * [data-ap-cart-hinweis], role=status) — ausserhalb der Bereiche, die
   * CartDrawerItems.onCartUpdate austauscht. renderContents ersetzt den Inhalt
   * dagegen ganz; deshalb wird der Hinweis ERST NACH dem Zeichnen gesetzt.
   * Solange er steht, beschreibt er den Dialog (aria-describedby): beim
   * Oeffnen liest ein Vorleseprogramm ihn mit dem Titel "Warenkorb" vor.
   */
  zeigeHinweis(text, art) {
    const inner = this.querySelector('.drawer__inner');
    let slot = this.querySelector('[data-ap-cart-hinweis]');
    if (!slot) {
      if (!text || !inner) return;
      slot = document.createElement('p');
      slot.id = 'CartDrawer-Hinweis';
      slot.className = 'ap-cart-hinweis';
      slot.setAttribute('role', 'status');
      slot.setAttribute('data-ap-cart-hinweis', '');
      const kopf = inner.querySelector('.drawer__header');
      if (kopf) kopf.insertAdjacentElement('afterend', slot);
      else inner.prepend(slot);
    }
    slot.classList.toggle('ap-cart-hinweis--fehler', art === 'fehler');
    slot.textContent = text || '';
    if (!inner) return;
    if (text) inner.setAttribute('aria-describedby', slot.id || 'CartDrawer-Hinweis');
    else inner.removeAttribute('aria-describedby');
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      // Montagecheck-Panel (.ap-cartc) mitziehen. Es liegt NEBEN .drawer__inner
      // und faellt deshalb nicht unter den Austausch darueber — ohne diesen
      // Eintrag zeigte es nach einer Mengenaenderung den alten Stand und
      // forderte z. B. einen Schlauch fuer einen gerade entfernten Reifen.
      //
      // Bewusst als LETZTER Eintrag: getSectionInnerHTML greift ohne
      // Null-Pruefung zu, also sind Warenkorb und Zaehler bereits aktualisiert,
      // falls hier je etwas fehlschlaegt. Das Element rendert deshalb auch bei
      // leerem Warenkorb immer mit (siehe snippets/cart-drawer.liquid).
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.ap-cartc__checkslot',
      },
    ];
  }

  // Der Weg, den eine Warenkorb-Aenderung aus einer ANDEREN Quelle nimmt (ein
  // Produktformular auf der Seite veroeffentlicht cartUpdate). Die Basisklasse
  // tauscht dabei nur cart-drawer-items und .cart-drawer__footer aus — das
  // Panel liegt ausserhalb von beidem und bliebe stehen. Gleiche Mechanik,
  // eine Auswahl mehr.
  onCartUpdate() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // RUECKFALLLINIE fuer "is-empty" am Host. Jeder Mutator schaltet die
        // Klasse aus SEINER Sicht um; hier steht der frisch gerenderte Stand vom
        // Server, also die Wahrheit. Vor dem Austausch weiter unten, weil `this`
        // (cart-drawer-items) dabei aus dem Dokument faellt.
        //
        // Ohne diese Zeile blieb die Klasse auf dem Standard-Actions-/WebMCP-Weg
        // haengen: standard-actions-override.js laesst den Drawer-Abschnitt
        // bewusst aus (er steht in DAWN_PUBSUB_REFRESHED_SECTIONS) und faellt
        // genau hierher zurueck, raeumt aber selbst nichts auf. Legte ein Agent
        // ueber Shopify.actions.updateCart in einen leer geladenen Korb und
        // oeffnete dann ueber actions.openCart, ging der Drawer leer-formatiert
        // auf — mit "Ihr Einkaufswagen ist leer" ueber einer gefuellten Liste.
        // Umgekehrt fehlte nach dem Entfernen der letzten Position der
        // Leer-Hinweis.
        const host = this.closest('cart-drawer');
        const frischerHost = html.querySelector('cart-drawer');
        if (host && frischerHost) {
          host.classList.toggle('is-empty', frischerHost.classList.contains('is-empty'));
        }

        // .cart-drawer__top muss mit: dort sitzen die Statuszeile des
        // Montagechecks und die Versandleiste. Ohne sie meldete der Streifen
        // nach einer Aenderung aus anderer Quelle weiter den alten Stand.
        // Seit R2 (28.09.2026) auch die Ueberschrift („Ihr Warenkorb (n)") und die
        // Vertrauenszeile des Fusses (Lieferspanne haengt an der Lieferklasse des Korbs).
        const selectors = [
          '.cart-drawer__top',
          'cart-drawer-items',
          '.cart-drawer__footer',
          '.ap-cartc__checkslot',
          '.drawer__heading',
          '[data-ap-cart-trust]',
        ];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
